<script setup lang="ts">
import BatchTypeHeader from '@/components/features/batches/tab-content/BatchTypeHeader.vue'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import { RouterLink } from 'vue-router'
import { Icon } from '@iconify/vue'
import {type Batch, BatchState} from '@/types/batch-types'
import { onMounted, ref } from 'vue'
import { getSupplier } from '@/constants/suppliers'
import { getSupplierList } from '@/api/suppliers'
import type { Supplier } from '@/types/supplier-types'
import type { BatchRoll} from "@/types/batch-types";
import type { Product } from '@/types/product-types'
import { getProductList } from '@/api/products'
import { getFinishedProduct } from '@/constants/productSpecification'
import {RollState} from "@/types/roll-types";

const props = defineProps<{
  batchList: Batch[]
}>()

const suppliers = ref<Supplier[]>([])
const products = ref<Product[]>([])

const isBatchInGreigForm = (batch: Batch) => {
  return batch.rolls && batch.rolls.some((roll: BatchRoll) => roll.rollState === RollState.Greig)
}

const getQualityId = (batch: Batch) => {
  return isBatchInGreigForm(batch) ? batch.knittingQualityId : batch.finishingQualityId
}

const getSupplierId = (batch: Batch) => {
  return isBatchInGreigForm(batch) ? batch.knittingSupplierId : batch.finishingSupplierId
}

const getProductName = (batch: Batch) => {
  const qualityId = getQualityId(batch)
  const isGreigForm = isBatchInGreigForm(batch)

  if (isGreigForm) {
    return products.value?.find((product: Product) => product.id === qualityId)?.unfinishedReference || 'Unknown Quality'
  } else {
    return qualityId ? getFinishedProduct(qualityId, products.value) : 'Unknown Quality'
  }
}

const getRollCount = (batch: Batch) => {
  if (!batch.rolls) return 0

  const isGreigForm = isBatchInGreigForm(batch)

  if (isGreigForm) {
    return batch.rolls.filter((roll: BatchRoll) => roll.isActive && roll.rollState === RollState.Greig).length
  } else {
    return batch.rolls.filter((roll: BatchRoll) => roll.isActive && roll.rollState === RollState.Offsite).length
  }
}

onMounted(async () => {
  try {
    products.value = await getProductList()
  } catch (error) {
    console.error('Failed to load products:', error)
  }

  try {
    suppliers.value = await getSupplierList()
  } catch (error) {
    console.error('Failed to load suppliers:', error)
  }
})

</script>

<template>
  <BatchTypeHeader
    title="Offsite"
    description="Find all batches offsite or that have gone through the finishing Process."
    :state="BatchState.OFFSITE"
    :display-add-button="true"
  />

  <DataTable :value="batchList" class="px-0">
    <Column field="batchNumber" header="Batch Number">
      <template #body="slotProps">
        <RouterLink
            :to="`/batches/${slotProps.data.id}`"
            class="font-medium text-neutral-800 block hover:underline"
        >
          {{ slotProps.data.batchNumber }}
        </RouterLink>
      </template>
    </Column>
    <Column field="purchaseOrderReference" header="Purchase Order Ref.">
      <template #body="slotProps">
        <RouterLink
            :to="`/batches/${slotProps.data.id}`"
            class="font-medium text-neutral-800 block hover:underline"
        >
          {{ slotProps.data.purchaseOrderReference }}
        </RouterLink>
      </template>
    </Column>
    <Column field="quality" header="Batch Quality">
      <template #body="slotProps">
        <RouterLink
            :to="`/productSpecification/${getQualityId(slotProps.data)}`"
            class="font-medium text-neutral-800 block hover:underline"
        >
          {{ getProductName(slotProps.data) }}
        </RouterLink>
      </template>
    </Column>
    <Column field="supplier" header="Supplier">
      <template #body="slotProps">
        <RouterLink
            :to="`/suppliers/${getSupplierId(slotProps.data)}`"
            class="font-medium text-neutral-800 block hover:underline"
        >
          {{ getSupplierId(slotProps.data) ? getSupplier(getSupplierId(slotProps.data)!, suppliers) : 'Unknown Supplier' }}
        </RouterLink>
      </template>
    </Column>
    <Column field="rolls.length" header="Number of Rolls">
      <template #body="slotProps">
        {{ getRollCount(slotProps.data) }}
      </template>
    </Column>
    <Column field="action">
      <template #body="slotProps">
        <RouterLink
          :to="`/batches/${slotProps.data.id}`"
          class="flex items-center gap-x-2 group hover:text-primary-default"
        >
          <span>More details</span>
          <Icon
            icon="mdi:arrow-right"
            class="text-2xl group-hover:translate-x-2 transition-transform text-gray-500"
          />
        </RouterLink>
      </template>
    </Column>
  </DataTable>
</template>